import { storage } from './storage.js';
import { opaqueId } from './utils.js';
import { DROPBOX_KEY, MS_CLIENT_ID } from './flavor.js';
import { profiles } from './profiles.js';

/* Puente de almacenamiento: SMB (el NAS o el PC de casa) y carpetas del propio aparato.
   El navegador no sabe hablar SMB ni leer carpetas sueltas, así que lo hace la parte nativa de la app (el plugin
   de Android o Electron en Windows): levanta un servidor WebDAV de solo lectura en 127.0.0.1 y cada origen cuelga de
   él bajo un código distinto. Para el resto de la app es como una carpeta WebDAV más (files.js), con Range para
   adelantar y retroceder.

   El puerto se recuerda y se pide siempre el mismo: las direcciones de los vídeos que se guardan en el catálogo
   llevan el puerto, y no deben cambiar de un arranque al siguiente. */

export const PREFERRED_PORT = 18790;
const PORT_KEY = 'storageBridgePort';

/** Lo que ofrece este aparato, o null: { start(port) → {port}, register(def) → {ok}, pickFolder() → {uri, name}|null, kinds: ['smb','local'] }. */
export function nativeStorage() {
    try {
        const p = window.cordova && window.cordova.plugins && window.cordova.plugins.filotecaStorage;
        if (p) return { kinds: ['smb', 'local', 'dropbox', 'onedrive'], start: (port) => p.start(port), register: (def) => p.register(def), pickFolder: () => p.pickFolder() };
        const e = window.electronBridge && window.electronBridge.storage;
        // Electron devuelve los fallos como { error: código } (un Error no pasa entero por el puente de contexto).
        if (e) {
            const fixed = (r) => { if (r && r.error) throw Object.assign(new Error(r.error), { code: r.error }); return r; };
            return { kinds: e.kinds || ['local'], start: (port) => e.start(port).then(fixed), register: (def) => e.register(def).then(fixed), pickFolder: () => e.pickFolder().then(fixed) };
        }
    } catch (err) { /* sin puente */ }
    return null;
}

export function storageAvailable(kind) {
    const n = nativeStorage();
    return !!(n && (!kind || n.kinds.indexOf(kind) !== -1));
}

/** «smb://nas/Películas/Cine» → { server, share, path } (o null si no es una dirección SMB con carpeta compartida). */
export function parseSmbHost(host) {
    const m = /^smb:\/\/([^/\s]+)\/([^/\s]+)(?:\/(.*))?$/i.exec(String(host || '').trim().replace(/\\/g, '/'));
    if (!m) return null;
    return { server: m[1], share: m[2], path: String(m[3] || '').replace(/^\/+|\/+$/g, '') };
}

/** Lo que se escribe a mano (\\nas\Películas\Cine, smb://nas/Películas/Cine, nas/Películas) → smb://nas/Películas/Cine; "" si no cabe. */
export function smbHostFromInput(text) {
    const t = String(text || '').trim().replace(/^smb:\/\//i, '').replace(/\\/g, '/').replace(/^\/+/, '');
    const parts = t.split('/').filter(Boolean);
    return parts.length >= 2 ? smbHost({ server: parts[0], share: parts[1], path: parts.slice(2).join('/') }) : '';
}

export function smbHost({ server, share, path = '' }) {
    const clean = (s) => String(s || '').trim().replace(/\\/g, '/').replace(/^\/+|\/+$/g, '');
    const srv = clean(server).replace(/^smb:\/\//i, '').replace(/\/.*$/, '');
    if (!srv || !clean(share)) return '';
    return 'smb://' + srv + '/' + clean(share) + (clean(path) ? '/' + clean(path) : '');
}

/** El mismo origen da siempre el mismo código, también en otro aparato; las carpetas del aparato traen el suyo. */
export function bridgeToken(profile) {
    if (profile.type === 'local') return String(profile.host || '').replace(/^local:\/\//, '');
    return opaqueId(profile.type + '\n' + String(profile.host || '').toLowerCase() + '\n' + (profile.username || '')).slice(0, 20);
}

/** «dropbox:///Cine/Clásicos» → 'Cine/Clásicos' (la carpeta de la nube que hace de raíz; '' = toda la cuenta). */
export const cloudPath = (host) => String(host || '').replace(/^[a-z]+:\/\/\/?/i, '').replace(/^\/+|\/+$/g, '');
export const cloudHost = (type, path) => type + ':///' + String(path || '').trim().replace(/\\/g, '/').replace(/^\/+|\/+$/g, '');
export const isCloud = (type) => type === 'dropbox' || type === 'onedrive';

export const newLocalHost = () => 'local://' + opaqueId('local' + Date.now() + Math.random()).slice(0, 20);

/** Datos que se le pasan a la parte nativa para este origen. */
export function bridgeDefinition(profile) {
    const token = bridgeToken(profile);
    if (profile.type === 'local') return { kind: 'local', token, uri: profile.folderUri || '', name: profile.name || '' };
    if (isCloud(profile.type)) {
        if (!profile.password) throw Object.assign(new Error('fields'), { code: 'fields' });
        return { kind: profile.type, token, clientId: profile.type === 'dropbox' ? DROPBOX_KEY : MS_CLIENT_ID, refreshToken: profile.password, path: cloudPath(profile.host) };
    }
    const smb = parseSmbHost(profile.host);
    if (!smb) throw Object.assign(new Error('fields'), { code: 'fields' });
    const user = String(profile.username || '');
    const slash = user.indexOf('\\');
    return { kind: 'smb', token, server: smb.server, share: smb.share, path: smb.path, domain: slash > 0 ? user.slice(0, slash) : '', user: slash > 0 ? user.slice(slash + 1) : user, pass: profile.password || '' };
}

let started = null;

/** Arranca el servidor local (una sola vez) y devuelve el puerto. */
export function startBridge(native = nativeStorage()) {
    if (!native) return Promise.reject(Object.assign(new Error('unavailable'), { code: 'unavailable' }));
    if (!started) {
        const wanted = Number(storage.get(PORT_KEY, 0)) || PREFERRED_PORT;
        started = native.start(wanted).then((r) => {
            const port = Number(r && r.port);
            if (!port) throw Object.assign(new Error('storage'), { code: 'storage' });
            storage.set(PORT_KEY, port);
            return port;
        }, (e) => { started = null; throw e; });
    }
    return started;
}

/** Deja el origen listo en el servidor local y devuelve la raíz (http://127.0.0.1:puerto/código/). */
export const storageBridge = {
    async ensure(profile, native = nativeStorage()) {
        const port = await startBridge(native);
        const def = bridgeDefinition(profile);
        const done = await native.register(def);
        // Microsoft renueva el refresh token cada vez: si cambió, se guarda el nuevo (si no, caduca el viejo a los 90 días sin uso).
        if (isCloud(profile.type) && done && done.refreshToken && done.refreshToken !== profile.password && profiles.list().some((p) => p.id === profile.id)) {
            await profiles.update(profile.id, { password: done.refreshToken }).catch(() => {});
        }
        return 'http://127.0.0.1:' + port + '/' + def.token + '/';
    },
    /** Elegir una carpeta del aparato: { uri, name } o null si se cancela. */
    async pickFolder(native = nativeStorage()) {
        if (!native) throw Object.assign(new Error('unavailable'), { code: 'unavailable' });
        const r = await native.pickFolder();
        return r && r.uri ? { uri: String(r.uri), name: String(r.name || '') } : null;
    },
    reset() { started = null; }
};
